import { create } from "zustand";

/**
 * Receiver-side download progress, attachmentId → 0-100.
 *
 * Written by the SHARED single-flight download in processMediaAttachment (not
 * by per-caller callbacks: runDeduplicated only executes the first caller's
 * closure, so a callback from a later joiner would be silently discarded —
 * a store lets every consumer subscribe by id and gives late joiners the
 * current value on their first render).
 *
 * Deliberately a SEPARATE store from UploadProgressState even though the shape
 * is identical: bubbles derive `isUploading` from that map's keys and render
 * the upload scrim + cancel affordance — writing download progress there would
 * dress every downloading attachment as an in-flight upload.
 *
 * Entries are OWNED: dedupeInFlight deliberately lets an aborted run keep
 * unwinding while a FRESH run for the same attachment starts, and both write
 * the same id-keyed entry — so setProgress records which run wrote it, and
 * clearProgress deletes only when the caller's token strictly matches the
 * stored one (both absent counts as a match). A dying run's cleanup can
 * therefore never wipe the fresh run's live ring.
 */
type DownloadProgressState = {
  progress: Record<string, number>; // attachmentId → 0-100
  owners: Record<string, number>; // attachmentId → token of the run that last wrote it
  setProgress: (attachmentId: string, value: number, owner?: number) => void;
  clearProgress: (attachmentId: string, owner?: number) => void;
};

export const useDownloadProgressState = create<DownloadProgressState>()(
  (set) => ({
    progress: {},
    owners: {},
    setProgress(attachmentId, value, owner) {
      set((state) => {
        const owners = { ...state.owners };
        if (owner === undefined) {
          delete owners[attachmentId]; // unowned write — anonymous clear may drop it
        } else {
          owners[attachmentId] = owner;
        }
        return {
          progress: {
            ...state.progress,
            [attachmentId]: Math.max(0, Math.min(100, Math.round(value))),
          },
          owners,
        };
      });
    },
    clearProgress(attachmentId, owner) {
      set((state) => {
        if (!(attachmentId in state.progress)) {
          return state;
        }
        if (state.owners[attachmentId] !== owner) {
          return state; // a fresh run owns this entry now — leave it alone
        }
        const progress = { ...state.progress };
        const owners = { ...state.owners };
        delete progress[attachmentId];
        delete owners[attachmentId];
        return { progress, owners };
      });
    },
  }),
);
